// Third Party
import axios, { AxiosProgressEvent } from 'axios';
import ono from 'ono';
import { v4 } from 'uuid';

export enum UploadErrors {
  MISSING_EXTENSION = 'ERR_MISSING_EXTENSION',
  UPLOAD_FAILED = 'ERR_UPLOAD_FAILED',
}

/**
 * Upload a file to the temporary uploads location.
 *
 * @param {string} bucketName The name of the S3 bucket where the file should be uploaded
 * @param {File} file The file that is being uploaded
 * @param {function} onProgress The progress callback function
 * @returns {string} The URL where the file was uploaded
 */
export async function uploadFile(
  bucketName: string,
  file: File,
  onProgress?: (event: AxiosProgressEvent) => void
): Promise<string> {
  const uuid = v4();
  const fileExt = file.name.split('.').pop();

  if (!fileExt) {
    throw ono(
      { code: UploadErrors.MISSING_EXTENSION },
      'The given file does not have an extension.'
    );
  }

  const uuidFileName = `${uuid}.${fileExt}`;
  const formData = new FormData();

  formData.append('key', uuidFileName);
  formData.append('file', file);

  const options = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (typeof onProgress === 'function') onProgress(progressEvent);
    },
  };

  try {
    await axios.post(
      `https://${bucketName}.s3.amazonaws.com`,
      formData,
      options
    );

    return `https://${bucketName}.s3.amazonaws.com/${uuidFileName}`;
  } catch (error) {
    const err = error as Error;

    throw ono(
      err,
      { code: UploadErrors.UPLOAD_FAILED },
      'The file upload failed.'
    );
  }
}
